import { useCallback, useRef, useState } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getErrorMessage } from '@/lib/api'
import { assignmentsApi } from '@/services/assignments'
import type { Assignment, JobScheduleWorkersOverview } from '@/types'

import { applyOptimisticAssignment, applyOptimisticRemoval, type ScheduleJobRow, type ScheduleJobWorker } from '../utils/job-rows'
import { jobAssignmentsQueryKey } from './use-job-panel-data'

/** Ids are numeric in the API; keep any non-numeric id as-is (same as the Real Time assign). */
const toApiId = (id: string) => {
  const numeric = Number(id)
  return Number.isFinite(numeric) ? numeric : id
}

/** Same payload the Real Time page (and Locations) uses to add a worker to a job without an open slot. */
const newAssignmentPayload = (jobId: string, workerId: string): Partial<Assignment> => ({
  job: toApiId(jobId),
  worker: toApiId(workerId),
  status: 'pending',
  pay_type: 'per_hour',
  pay: 0,
  night_pay: 0,
  holiday_pay: 0,
  peak_pay: 0,
  pay_flat: 0,
})

/**
 * Add / remove a worker on a job. Add fills the job's first open slot (assignment update; the
 * backend syncs the work-day entries) or creates an assignment when there is none. Remove deletes
 * the worker's assignment (and with it the slot), like the Real Time page. The overview cache
 * updates at once and rolls back on failure; the overview and the job's assignments are refetched
 * either way. One request per row at a time.
 */
export function useAssignWorker(queryKey: QueryKey) {
  const queryClient = useQueryClient()
  const pendingRef = useRef(new Set<string>())
  const [pendingRowKeys, setPendingRowKeys] = useState<ReadonlySet<string>>(() => new Set())

  const setPending = useCallback((rowKey: string, pending: boolean) => {
    if (pending) pendingRef.current.add(rowKey)
    else pendingRef.current.delete(rowKey)
    setPendingRowKeys(new Set(pendingRef.current))
  }, [])

  const run = useCallback(async (
    row: ScheduleJobRow,
    optimistic: (data: JobScheduleWorkersOverview) => JobScheduleWorkersOverview,
    request: () => Promise<unknown>,
    messages: { success: string; failure: string },
  ) => {
    if (pendingRef.current.has(row.key)) {
      return false
    }
    setPending(row.key, true)
    await queryClient.cancelQueries({ queryKey })
    const previous = queryClient.getQueryData<JobScheduleWorkersOverview | null>(queryKey)
    if (previous) {
      queryClient.setQueryData(queryKey, optimistic(previous))
    }

    try {
      await request()
      toast.success(messages.success)
      return true
    } catch (error) {
      // Restore the snapshot only when no other change is in flight (it would undo that one too).
      if (previous && pendingRef.current.size === 1) {
        queryClient.setQueryData(queryKey, previous)
      }
      toast.error(messages.failure, {
        description: getErrorMessage(error, 'No changes were made. Please try again.'),
      })
      return false
    } finally {
      setPending(row.key, false)
      void queryClient.invalidateQueries({ queryKey })
      void queryClient.invalidateQueries({ queryKey: jobAssignmentsQueryKey(row.jobId) })
    }
  }, [queryClient, queryKey, setPending])

  const assign = useCallback((row: ScheduleJobRow, worker: ScheduleJobWorker) => {
    const assignmentId = row.openAssignmentIds[0] ?? null
    return run(
      row,
      (data) => applyOptimisticAssignment(data, row, assignmentId, worker),
      () => (assignmentId
        ? assignmentsApi.updateAssignment(assignmentId, { worker: toApiId(worker.workerId) })
        : assignmentsApi.createAssignment(newAssignmentPayload(row.jobId, worker.workerId))),
      { success: `${worker.name} assigned to ${row.title}`, failure: `Couldn't assign ${worker.name} to ${row.title}` },
    )
  }, [run])

  const remove = useCallback((row: ScheduleJobRow, worker: ScheduleJobWorker & { assignmentId: string }) => run(
    row,
    (data) => applyOptimisticRemoval(data, row.jobId, worker.workerId),
    () => assignmentsApi.deleteAssignment(worker.assignmentId),
    { success: `${worker.name} removed from ${row.title}`, failure: `Couldn't remove ${worker.name} from ${row.title}` },
  ), [run])

  return { assign, remove, pendingRowKeys }
}
